import React, { useMemo, useState, useEffect, useContext, useCallback, useRef } from "react";
import styled from "styled-components";
import gsap, { Draggable, InertiaPlugin } from "gsap/all";
import { CursorContext, ScreenContext } from "components/Providers";

import text from "styles/text";
import media from "styles/media";
import colors from "styles/colors";

import { zeroPad, vwToPx } from "utils/functions";
import { useMedia } from "utils/hooks";

import Card from "components/cards/Roadmap";
import HeadingAnimation from "components/HeadingAnimation";

import BitsMaskPNG from "assets/images/bitsMask.png";
import ResponsiveBitsMaskPNG from "assets/images/fullBitsMask.png";
import FreshcutOvalSVG from "assets/images/icons/freshcutOval.svg";
import ArrowSVG from "assets/images/icons/bitArrow.svg";

gsap.registerPlugin(Draggable, InertiaPlugin);

const DATA = [
  {
    title: "FreshCut app launched on ios",
    completed: true,
    trigger: 0,
  },
  {
    title: "FreshCut creator partnerships program launched 🚀",
    completed: false,
    trigger: 0,
  },
  {
    title: "FreshCut Diamonds Private Placement Sale via Republic Crypto",
    completed: false,
    trigger: 0,
  },
  {
    title: "FreshCut sponsor- ed community events with $FCD rewards",
    completed: false,
    trigger: 0,
  },
  {
    title: "$FCD membership program and benefits launched",
    completed: false,
    trigger: 0,
  },
  {
    title: "$FCD integrated into FreshCut app",
    completed: false,
    trigger: 1,
  },
  {
    title: "Create-and-earn $FCD in app",
    completed: false,
    trigger: 1,
  },
  {
    title: "Engage-and-earn $FCD in app",
    completed: false,
    trigger: 1,
  },
  {
    title: "Creator tipping with $FCD",
    completed: false,
    trigger: 1,
  },
  {
    title: "FreshCut DAO launched",
    completed: false,
    trigger: 1,
  },
  {
    title: "IRL event token activations",
    completed: false,
    trigger: 2,
  },
  {
    title: "Additional utility added for $FCD in-app and ecosystem",
    completed: false,
    trigger: 2,
  },
];

const Roadmap: React.FC = () => {
  const cursor = useContext(CursorContext);
  const screen = useContext(ScreenContext);

  const [innerRef, setInnerRef] = useState<HTMLDivElement | null>(null);
  const columnRef = useRef<HTMLDivElement | null>(null);
  const [activeCard, setActiveCard] = useState<number>(0);

  const cardWidth = useMedia(38.89, 38.89, 54.68, 92.27);
  const bitsMaskSrc = useMedia(BitsMaskPNG, BitsMaskPNG, ResponsiveBitsMaskPNG, ResponsiveBitsMaskPNG);
  const titleTopPerc = useMedia(41, 41, 57, 41);
  const titleBotPerc = useMedia(27, 27, 44, 27);
  const columnTop = useMedia(vwToPx(1.53), vwToPx(1.53), vwToPx(2.15), vwToPx(5.867));

  const cursorEnter = () => {
    if (screen.mobile || screen.tablet) {
      cursor.setValue({ roadmap: false, banners: false })
    } else {
      cursor.setValue({ roadmap: true, banners: false })
    }
  }

  const handleLeftClick = (disabled: boolean) => {
    if (!disabled) {
      let index = activeCard - 1;

      setActiveCard(index);
      setQuarter(index);
    }
  };

  const handleRightClick = (disabled: boolean) => {
    if (!disabled) {
      let index = activeCard + 1;

      setActiveCard(index);
      setQuarter(index);
    }
  };

  const moveInner = useCallback(() => {
    if (innerRef) {
      gsap.to(innerRef, {
        duration: 0.5,
        x: `-${activeCard * cardWidth}vw`,
      });
    }
  }, [activeCard, cardWidth, innerRef]);

  const generateSnapArray = useCallback(() => {
    let snapArray = DATA.map((item, index) => {
      // the drag instance is fixed to 3 decimal spaces.
      // this is necessary to find the index of the snap value
      return Number((index * -vwToPx(cardWidth)).toFixed(3));
    });

    return snapArray;
  }, [cardWidth]);

  const setQuarter = useCallback(
    (index: number) => {
      let { trigger } = DATA[index];

      gsap.to(columnRef.current, {
        y: -columnTop * trigger,
      });
    },
    [columnTop]
  );

  useEffect(() => {
    moveInner();
  }, [activeCard, moveInner]);

  useEffect(() => {
    if (innerRef) {
      const snap = generateSnapArray();

      const drag = Draggable.create(innerRef, {
        type: "x",
        inertia: true,
        zIndexBoost: false,
        snap,
        onThrowComplete: () => {
          const index = snap.indexOf(drag[0].x);
          setQuarter(index);
          setActiveCard(index);
        },
      });

      drag[0].vars.cursor = "none";
    }
  }, [innerRef, generateSnapArray, setQuarter]);

  const cards = useMemo(
    () =>
      DATA.map((item, index) => {
        return (
          <CardWrapper key={index}>
            <Card key={index} number={zeroPad(index + 1)} title={item.title} completed={item.completed} />
          </CardWrapper>
        );
      }),
    []
  );

  return (
    <Wrapper>
      <Oval src={FreshcutOvalSVG} alt="freshcut oval" loading="lazy"/>
      <Arrow src={ArrowSVG} alt="bit arrow" loading="lazy"/>
      <BitsMask src={bitsMaskSrc} alt="some gradient squares" loading="lazy"/>
      <Title>
        <HeadingAnimation text="Our Roadmap" topPerc={titleTopPerc} botPerc={titleBotPerc} />
      </Title>
      <Quarter>
        <div>Q</div>
        <Column ref={columnRef}>
          <p>2</p>
          <p>3</p>
          <p>4</p>
        </Column>
        <div>&nbsp;</div>
        <div>2</div>
        <div>0</div>
        <div>2</div>
        <div>2</div>
      </Quarter>
      <Inner ref={(ref) => setInnerRef(ref)} onMouseEnter={cursorEnter} onMouseLeave={() => cursor.setValue({ roadmap: false, banners: false })}>
        {cards}
      </Inner>
      <SliderArrows>
        <LeftArrow src={ArrowSVG} alt="bit arrow" loading="lazy" disabled={activeCard === 0} onClick={() => handleLeftClick(activeCard === 0)} />
        <RightArrow src={ArrowSVG} alt="bit arrow" loading="lazy" disabled={activeCard === DATA.length - 1} onClick={() => handleRightClick(activeCard === DATA.length - 1)} />
      </SliderArrows>
    </Wrapper>
  );
};

export default Roadmap;

const Wrapper = styled.section`
  width: 100%;
  position: relative;

  height: 78.75vw;
  padding-bottom: 20.83vw;

  ${media.tablet} {
    height: 115.23vw;
    padding-bottom: 19.53vw;
  }

  ${media.mobile} {
    height: 231.733vw;
    padding-bottom: 66.667vw;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  p {
    height: 1.53vw;

    ${media.tablet} {
      height: 2.15vw;
    }
    ${media.mobile} {
      height: 5.867vw;
    }
  }
`;

const Quarter = styled.div`
  ${text.desktop.h6}
  color: ${colors.black50};
  position: absolute;
  display: flex;
  overflow: hidden;

  top: 21.25vw;
  left: 50.69vw;
  height: 1.53vw;

  ${media.tablet} {
    ${text.tablet.h6}
    top: 62.3vw;
    left: 5.86vw;
    height: 2.15vw;
  }

  ${media.mobile} {
    ${text.mobile.h6}
    top: 87.2vw;
    left: 4vw;
    height: 5.867vw;
  }
`;

const Inner = styled.div`
  position: absolute;
  display: flex;
  overflow: hidden;
  cursor: none;

  top: 24.17vw;
  left: 50.69vw;

  ${media.tablet} {
    top: 66.41vw;
    left: 5.86vw;
  }
  ${media.mobile} {
    top: 98.4vw;
    left: 4vw;
  }
`;

const CardWrapper = styled.div`
  margin-right: 1.39vw;

  ${media.tablet} {
    margin-right: 1.95vw;
  }
  ${media.mobile} {
    margin-right: 5.33vw;
  }
`;

const BitsMask = styled.img`
  position: absolute;
  z-index: 2;
  pointer-events: none;

  width: 39.58vw;
  height: 47.5vw;
  left: 0vw;
  top: 10.42vw;

  ${media.tablet} {
    width: 89.06vw;
    top: 14.65vw;
    left: 0vw;
    height: 44.53vw;
  }

  ${media.mobile} {
    width: 121.6vw;
    height: 60.8vw;
    top: 0;
  }
`;

const Title = styled.h5`
  ${text.desktop.h3}
  position: absolute;
  z-index: 3;
  text-transform: uppercase;
  pointer-events: none;

  width: 45.5vw;
  top: 24.65vw;
  left: 4.17vw;

  ${media.tablet} {
    ${text.tablet.h2}
    letter-spacing: -0.05em;
    width: 81.66vw;
    top: 20.31vw;
    left: 5.86vw;
  }
  ${media.mobile} {
    ${text.mobile.h1}
    width: 92vw;
  }
`;

const Oval = styled.img`
  position: absolute;

  width: 5.21vw;
  height: 2.71vw;
  left: 25.14vw;
  top: 12.99vw;

  ${media.tablet} {
    left: 79.88vw;
    width: 7.32vw;
    height: 3.81vw;
    top: 18.46vw;
  }
  ${media.mobile} {
    width: 9.867vw;
    height: 5.067vw;
    left: 78.667vw;
    top: 5.067vw;
  }
`;

const Arrow = styled.img`
  position: absolute;
  z-index: 3;
  transform: rotate(-90deg);

  width: 5.83vw;
  height: 4.58vw;
  left: 4.17vw;
  top: 15.28vw;

  ${media.tablet} {
    left: 17.58vw;
    top: 47.17vw;
    width: 8.2vw;
    height: 6.45vw;
  }
  ${media.mobile} {
    width: 20vw;
    height: 15.733vw;
    left: 23.2vw;
    top: 53.133vw;
  }
`;

const SliderArrows = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 4.93vw;
  left: 50.69vw;
  top: 47.22vw;

  ${media.tablet} {
    width: 6.933vw;
    left: 86.933vw;
    top: 61.867vw;
  }
  ${media.mobile} {
    width: 18.933vw;
    left: 77.067vw;
    top: 87.2vw;
  }
`;

const SliderArrow = styled.img<{ disabled?: boolean }>`
  cursor: pointer;

  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  
  width: 1.94vw;
  height: 1.53vw;

  ${media.tablet} {
    width: 2.73vw;
    height: 2.15vw;
  }
  ${media.mobile} {
    width: 7.467vw;
    height: 5.867vw;
  }
`;

const LeftArrow = styled(SliderArrow)`
  transform: rotate(90deg);
`;

const RightArrow = styled(SliderArrow)`
  transform: rotate(-90deg);
`;
